import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { NanoID } from '../../../../../../../types';
import {
  UniversalFilterCompaniesValue,
  UniversalFilterCompaniesModes,
  UniversalFilterCompaniesFilterState,
  UniversalFilterCompaniesOnFilterStateChange,
  UniversalFilterCompaniesOnChange
} from '../../UniversalFilterCompanies.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterCompaniesFilterState } from '../useUniversalFilterCompaniesFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterCompaniesModes.CONTAINS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterCompaniesModes>[] =
  [
    {
      value: UniversalFilterCompaniesModes.CONTAINS_ANY_OF,
      i18nText: filtersKeys.modes.containsAnyOf
    }
  ];

interface UniversalFilterCompaniesOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterCompaniesValue;
  onChange: UniversalFilterCompaniesOnChange;
}

function useUniversalFilterCompanies({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterCompaniesOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterCompaniesFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterCompaniesFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterCompaniesFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          companyNanoIds: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterCompaniesOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterCompaniesModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        companyNanoIds: filterState.companyNanoIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleCompanyIdsChange = useCallback<
    (companyNanoIds: NanoID[]) => void
  >(
    (companyNanoIds) => {
      handleFilterStateChange({
        mode: filterState.mode,
        companyNanoIds: companyNanoIds || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleCompanyIdsChange
  };
}

export default useUniversalFilterCompanies;
